import { useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import { useQuote } from "@/context/QuoteContext";
import { testIds } from "@/constants/testIds";
import { toast } from "sonner";
import {
  Minus,
  Plus,
  Trash2,
  Send,
  MessageCircle,
  Copy,
  ShoppingCart,
  Calendar,
  FileDown,
  Ban,
} from "lucide-react";
import { Link } from "react-router-dom";

export default function Quote() {
  const {
    lines,
    setQty,
    removeItem,
    clear,
    subtotal,
    subtotalBeforeDiscount,
    discountPercent,
    discountAmount,
    total,
    days,
    startDate,
    endDate,
    setStartDate,
    setEndDate,
    availability,
    avLoading,
  } = useQuote();
  const [form, setForm] = useState({
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [lastQuoteId, setLastQuoteId] = useState(null);

  useEffect(() => {
    if (endDate && startDate && new Date(endDate) < new Date(startDate)) {
      setEndDate(startDate);
    }
  }, [startDate, endDate, setEndDate]);

  const hasDates = !!(startDate && endDate);

  const overCap = lines.filter((l) => {
    const av = availability[l.item_id];
    return hasDates && av && l.quantity > av.available_qty;
  });

  const buildSummary = () => {
    const linesTxt = lines
      .map(
        (l) =>
          `• ${l.quantity}x ${l.name} — €${(l.daily_price * l.quantity).toFixed(
            2,
          )}/giorno`,
      )
      .join("\n");
    const dates =
      startDate && endDate
        ? startDate === endDate
          ? startDate
          : `${startDate} → ${endDate}`
        : "-";
    return `SPECTRUM — Richiesta Preventivo Noleggio\n\nCliente: ${
      form.customer_name || "-"
    }\nData evento: ${dates}\nGiorni di noleggio: ${days}\n\n${linesTxt}\n\nSubtotale/giorno: €${subtotal.toFixed(
      2,
    )}\nTotale (${days} gg): €${total.toFixed(2)}\n${
      form.notes ? `\nNote: ${form.notes}` : ""
    }`;
  };

  const submit = async () => {
    if (lines.length === 0) return toast.error("Aggiungi almeno un articolo");
    if (!form.customer_name.trim())
      return toast.error("Inserisci il tuo nome");
    if (overCap.length > 0)
      return toast.error(
        "Alcuni articoli superano la disponibilità per le date scelte",
      );
    setSubmitting(true);
    try {
      const { data } = await api.post("/quotes", {
        ...form,
        event_start_date: startDate || "",
        event_end_date: endDate || startDate || "",
        days,
        lines: lines.map((l) => ({
          item_id: l.item_id,
          name: l.name,
          daily_price: l.daily_price,
          quantity: l.quantity,
        })),
      });
      toast.success("Preventivo inviato! Ti abbiamo mandato una notifica.");
      setLastQuoteId(data.id);
      clear();
      setForm({
        customer_name: "",
        customer_email: "",
        customer_phone: "",
        notes: "",
      });
    } catch (e) {
      toast.error("Errore nell'invio del preventivo");
    } finally {
      setSubmitting(false);
    }
  };

  const copyQuote = async () => {
    await navigator.clipboard.writeText(buildSummary());
    toast.success("Preventivo copiato negli appunti");
  };

  const sendWhatsapp = () => {
    const url = `https://wa.me/?text=${encodeURIComponent(buildSummary())}`;
    window.open(url, "_blank");
  };

  if (lines.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center">
        <div className="inline-flex w-16 h-16 rounded-full border border-zinc-800 items-center justify-center text-zinc-600 mb-4">
          <ShoppingCart size={28} />
        </div>
        <h2 className="font-heading text-2xl sm:text-3xl font-bold uppercase text-white">
          Il tuo preventivo è vuoto
        </h2>
        <p className="text-zinc-500 mt-2 text-sm">
          Torna al catalogo e aggiungi gli articoli che ti servono.
        </p>
        {lastQuoteId && (
          <div className="mt-6 max-w-md mx-auto bg-zinc-900/90 border border-emerald-500/30 rounded-xl p-5 text-left">
            <div className="text-xs font-mono uppercase tracking-widest text-emerald-400 mb-2">
              ✓ Preventivo inviato
            </div>
            <p className="text-sm text-zinc-400 mb-3">
              Ti abbiamo inviato una notifica. Scarica il PDF del tuo preventivo:
            </p>
            <a
              data-testid={testIds.quotePdfBtn}
              href={`${API}/quotes/${lastQuoteId}/pdf`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 min-h-[44px] px-4 rounded-lg bg-rose-600 hover:bg-rose-500 text-white font-bold text-xs uppercase tracking-wider shadow-lg shadow-rose-900/30 transition"
            >
              <FileDown size={14} /> Scarica PDF
            </a>
          </div>
        )}
        <Link
          to="/"
          className="inline-flex items-center gap-2 mt-6 min-h-[44px] px-5 rounded-lg bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-white font-bold text-sm uppercase tracking-wider transition"
        >
          Vai al catalogo
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
      <h1 className="font-heading text-2xl sm:text-4xl font-black uppercase tracking-tight text-white mb-6">
        Il tuo <span className="text-rose-500">preventivo</span>
      </h1>

      <div className="grid lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3 space-y-3">
          {/* Date range */}
          <div className="bg-zinc-900/90 border border-zinc-800 rounded-xl p-4">
            <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">
              <Calendar size={12} className="text-rose-500" /> Date evento
              {avLoading && (
                <span className="text-zinc-600">· verifico disponibilità...</span>
              )}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">
                  Inizio
                </label>
                <input
                  data-testid={testIds.quoteStartDate}
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full min-h-[44px] px-3 rounded-lg bg-zinc-950 border border-zinc-800 focus:border-rose-600/60 focus:outline-none text-white text-sm"
                />
              </div>
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">
                  Fine
                </label>
                <input
                  data-testid={testIds.quoteEndDate}
                  type="date"
                  value={endDate}
                  min={startDate || undefined}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full min-h-[44px] px-3 rounded-lg bg-zinc-950 border border-zinc-800 focus:border-rose-600/60 focus:outline-none text-white text-sm"
                />
              </div>
            </div>
            <div
              data-testid={testIds.quoteDaysBadge}
              className="mt-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-rose-600/15 border border-rose-500/30 text-rose-300 text-[10px] font-mono uppercase tracking-widest"
            >
              {days} {days === 1 ? "giorno" : "giorni"} di noleggio
            </div>
          </div>

          {/* Lines */}
          {lines.map((l) => {
            const av = availability[l.item_id];
            const over = hasDates && av && l.quantity > av.available_qty;
            return (
              <div
                key={l.item_id}
                className={`bg-zinc-900/90 border rounded-xl p-3 sm:p-4 ${
                  over ? "border-rose-800" : "border-zinc-800"
                }`}
              >
                <div className="flex items-start gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="font-heading font-bold text-white text-sm sm:text-base break-words">
                      {l.name}
                    </div>
                    <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                      <span className="font-mono text-xs text-zinc-500">
                        €{l.daily_price.toFixed(2)}/gg
                      </span>
                      {over && (
                        <span className="text-[10px] font-mono uppercase text-rose-400 flex items-center gap-1">
                          <Ban size={10} /> max {av.available_qty} disp.
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    data-testid={testIds.removeFromQuoteBtn(l.item_id)}
                    onClick={() => removeItem(l.item_id)}
                    className="shrink-0 w-9 h-9 flex items-center justify-center text-zinc-600 hover:text-rose-500 active:scale-90 transition"
                    aria-label="Rimuovi"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
                <div className="flex items-center gap-2 justify-between mt-2 pt-2 border-t border-zinc-800/60">
                  <div className="flex items-center gap-1 bg-zinc-950 border border-zinc-800 rounded-lg shrink-0">
                    <button
                      data-testid={testIds.itemQtyMinus(l.item_id)}
                      onClick={() => setQty(l.item_id, l.quantity - 1)}
                      className="w-9 h-9 flex items-center justify-center text-zinc-400 hover:text-white active:scale-90 transition"
                    >
                      <Minus size={14} />
                    </button>
                    <span className="w-6 text-center font-mono font-bold text-white text-sm">
                      {l.quantity}
                    </span>
                    <button
                      data-testid={testIds.itemQtyPlus(l.item_id)}
                      onClick={() => setQty(l.item_id, l.quantity + 1)}
                      className="w-9 h-9 flex items-center justify-center text-zinc-400 hover:text-white active:scale-90 transition"
                    >
                      <Plus size={14} />
                    </button>
                  </div>
                  <div className="font-mono font-bold text-emerald-400 text-sm sm:text-base">
                    €{(l.daily_price * l.quantity).toFixed(2)}
                  </div>
                </div>
              </div>
            );
          })}

          <button
            data-testid={testIds.quoteClear}
            onClick={clear}
            className="text-xs text-zinc-500 hover:text-rose-500 font-mono uppercase tracking-widest mt-2"
          >
            Svuota preventivo
          </button>
        </div>

        <div className="lg:col-span-2 space-y-3">
          <div className="bg-zinc-900/90 border border-zinc-800 rounded-xl p-5 space-y-3">
            <h2 className="font-heading font-bold uppercase text-white text-sm tracking-wider mb-1">
              I tuoi dati
            </h2>
            <input
              data-testid={testIds.quoteCustomerName}
              placeholder="Nome e cognome *"
              value={form.customer_name}
              onChange={(e) =>
                setForm({ ...form, customer_name: e.target.value })
              }
              className="w-full min-h-[44px] px-3.5 rounded-lg bg-zinc-950 border border-zinc-800 focus:border-rose-600/60 focus:outline-none text-white placeholder-zinc-600 text-sm"
            />
            <input
              data-testid={testIds.quoteCustomerEmail}
              type="email"
              placeholder="Email"
              value={form.customer_email}
              onChange={(e) =>
                setForm({ ...form, customer_email: e.target.value })
              }
              className="w-full min-h-[44px] px-3.5 rounded-lg bg-zinc-950 border border-zinc-800 focus:border-rose-600/60 focus:outline-none text-white placeholder-zinc-600 text-sm"
            />
            <input
              data-testid={testIds.quoteCustomerPhone}
              placeholder="Telefono"
              value={form.customer_phone}
              onChange={(e) =>
                setForm({ ...form, customer_phone: e.target.value })
              }
              className="w-full min-h-[44px] px-3.5 rounded-lg bg-zinc-950 border border-zinc-800 focus:border-rose-600/60 focus:outline-none text-white placeholder-zinc-600 text-sm"
            />
            <textarea
              data-testid={testIds.quoteNotes}
              placeholder="Note aggiuntive (indirizzo evento, orari, richieste speciali...)"
              rows={3}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="w-full px-3.5 py-3 rounded-lg bg-zinc-950 border border-zinc-800 focus:border-rose-600/60 focus:outline-none text-white placeholder-zinc-600 text-sm resize-none"
            />
          </div>

          <div className="bg-gradient-to-br from-zinc-900 to-zinc-950 border border-rose-900/40 rounded-xl p-5 space-y-2 shadow-xl shadow-rose-950/20">
            <div className="flex justify-between text-xs font-mono text-zinc-500">
              <span>SUBTOTALE / GIORNO</span>
              <span>€{subtotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-xs font-mono text-zinc-500">
              <span>GIORNI</span>
              <span>× {days}</span>
            </div>
            {discountPercent > 0 && (
              <>
                <div className="flex justify-between text-xs font-mono text-zinc-500 pt-2 border-t border-zinc-800">
                  <span>SUBTOTALE</span>
                  <span>€{subtotalBeforeDiscount.toFixed(2)}</span>
                </div>
                <div
                  data-testid={testIds.quoteDiscountBadge}
                  className="flex justify-between text-xs font-mono text-emerald-400"
                >
                  <span>SCONTO MULTI-GIORNO (-{discountPercent.toFixed(0)}%)</span>
                  <span>– €{discountAmount.toFixed(2)}</span>
                </div>
              </>
            )}
            <div className="pt-2 border-t border-zinc-800 flex justify-between items-baseline">
              <span className="font-heading font-bold uppercase text-white">
                Totale
              </span>
              <span
                data-testid={testIds.quoteTotal}
                className="font-mono font-black text-2xl sm:text-3xl text-rose-500"
              >
                €{total.toFixed(2)}
              </span>
            </div>
          </div>

          {overCap.length > 0 && (
            <div className="bg-rose-950/30 border border-rose-800/60 text-rose-300 text-xs rounded-lg p-3 font-mono">
              <Ban size={12} className="inline mr-1" /> Alcuni articoli superano
              la disponibilità nelle date scelte. Riduci le quantità o cambia
              date.
            </div>
          )}

          <button
            data-testid={testIds.quoteSubmit}
            onClick={submit}
            disabled={submitting || overCap.length > 0}
            className="w-full min-h-[52px] rounded-xl bg-rose-600 hover:bg-rose-500 text-white font-heading font-black uppercase tracking-wider shadow-lg shadow-rose-900/40 transition active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            <Send size={16} />
            {submitting ? "Invio..." : "Richiedi preventivo"}
          </button>
          <div className="grid grid-cols-2 gap-2">
            <button
              data-testid={testIds.quoteWhatsapp}
              onClick={sendWhatsapp}
              className="min-h-[44px] rounded-lg bg-emerald-600/15 hover:bg-emerald-600/25 border border-emerald-500/30 text-emerald-300 font-semibold text-xs uppercase tracking-wider transition flex items-center justify-center gap-1.5"
            >
              <MessageCircle size={14} /> WhatsApp
            </button>
            <button
              data-testid={testIds.quoteCopy}
              onClick={copyQuote}
              className="min-h-[44px] rounded-lg bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-zinc-200 font-semibold text-xs uppercase tracking-wider transition flex items-center justify-center gap-1.5"
            >
              <Copy size={14} /> Copia
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
